/**
 * Co-dependent enum machinery — the complete flow for controlling-field enum resolution:
 *
 *   1. collectControllingKeys   — identify which fields gate another field's options
 *   2. normalizeControllingValues — resolve human labels to PISA codes for the controlling fields
 *   3. filterEnumByConstraints  — filter dependent enum options to those valid for the known codes
 *
 * Previously this logic was spread across three files:
 *   - enum-constraints.ts       → filterEnumByConstraints (now here)
 *   - get_write_schema.ts       → collectControllingKeys + normalizeControllingValues (now here)
 *   - field-mapper.ts           → called filterEnumByConstraints; still holds the fixed-point loop
 *
 * Both `get_write_schema` and `field-mapper` import from this single module so only one file
 * needs to be swapped when the data-access layer changes (Phase 8a portability seam).
 */

import { findBestEnumMatch } from './enum-matcher'
import type { CachedSchema } from '@/Atomic/Widgets/AiChatBot/core/engine/types'

type EnumOption = {
  constraints?: { [controllingKey: string]: string[] }
}

/**
 * Filter enum options to those valid for the supplied controlling-field values.
 *
 * An option passes when, for every constraint key it declares that also appears in
 * `knownValues`, the known value is in the option's allowed list. Options with no
 * constraints always pass. Constraint keys not present in `knownValues` are ignored
 * (the controlling field is not yet known, so we cannot filter on it).
 *
 * @param options     The dependent field's enum options (each may carry `constraints`).
 * @param knownValues Controlling-field values chosen/known so far, keyed by field name.
 */
export function filterEnumByConstraints<T extends EnumOption>(
  options: T[],
  knownValues: Record<string, string>,
): T[] {
  return options.filter((option) => {
    if (!option.constraints) {
      return true
    }
    for (const [controllingKey, allowed] of Object.entries(
      option.constraints,
    )) {
      const known = knownValues[controllingKey]
      if (known !== undefined && !allowed.includes(known)) {
        return false
      }
    }
    return true
  })
}

/**
 * Every field key referenced by any enum option's `constraints` in this schema —
 * i.e. the controlling fields (e.g. `group` for `status`, `type`/`subType` for Activity).
 */
export function collectControllingKeys(schema: CachedSchema): Set<string> {
  const keys = new Set<string>()
  for (const attr of schema.attributes) {
    for (const value of attr.values ?? []) {
      for (const controllingKey of Object.keys(value.constraints ?? {})) {
        keys.add(controllingKey)
      }
    }
  }
  return keys
}

/**
 * Resolve controlling-field values from human labels to PISA codes before filtering.
 *
 * The model refers to enum values by label (e.g. "Task"), but filterEnumByConstraints
 * matches on codes (e.g. "TASK"), so an unresolved label filters every dependent option
 * out (empty result). Values already in code form pass through unchanged (exact code
 * match wins).
 *
 * This is the shared resolver for both `get_write_schema` (knownFields on create) and
 * any path that needs to normalise a controlling-value map derived from a CachedSchema.
 */
export function normalizeControllingValues(
  schema: CachedSchema,
  values: Record<string, string>,
): Record<string, string> {
  const normalized: Record<string, string> = {}
  for (const [key, raw] of Object.entries(values)) {
    const options = schema.attributes.find((a) => a.name === key)?.values
    if (!options || options.length === 0) {
      normalized[key] = raw
      continue
    }
    const byCode = options.find(
      (o) => o.value.toLowerCase() === raw.toLowerCase().trim(),
    )
    if (byCode) {
      normalized[key] = byCode.value
      continue
    }
    const matchedLabel = findBestEnumMatch(
      raw,
      options.map((o) => o.label),
    )
    normalized[key] =
      options.find((o) => o.label === matchedLabel)?.value ?? raw
  }
  return normalized
}
